import { useConfirm } from "@/components/DialogProvider";
import { ScheduleForm } from "@/components/schedule/ScheduleForm";
import { FileIcon } from "@/components/shadcn-custom/ShadcnFileLoader";
import { MultiSelectCombobox } from "@/components/shadcn-custom/MultiSelectCombobox";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { HtmlContent } from "@/components/ui/html-content";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PlacesAutocomplete } from "@/components/ui/places-autocomplete";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { SearchableSelectPopover } from "@/components/ui/searchable-select-popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { WorkUnitsSection } from "@/components/work-units";
import { Link } from "@tanstack/react-router";
import { useAuth } from "@/contexts/AuthContext";
import { useProjectContext } from "@/contexts/ProjectContext";
import { useAgencies } from "@/features/agencies/hooks/useAgencies";
import { useProjectStatuses } from "@/features/config/hooks/useConfig";
import { useCustomerOperations } from "@/features/customers/hooks/useCustomers";
import * as projectService from "@/features/projects/api/projectService";
import { useUsers } from "@/features/users/hooks/useUsers";
import { storage } from "@/firebase";
import type { ProjectJobLocation, ProjectModel } from "@/firestore_types";
import {
  getJobLocationAddress,
  isProjectJobLocation,
  normalizeJobLocationForSave,
} from "@/lib/jobLocation";
import { dateStringToNewYorkDate, formatDateTimeShort } from "@/lib/utils";
import * as Sentry from "@sentry/react";
import { createFileRoute } from "@tanstack/react-router";
import { Timestamp } from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { Calendar, Save, Upload, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

// Helper function to trigger native date/time picker
const triggerPicker = (elementId: string) => {
  const element = document.getElementById(elementId) as HTMLInputElement | null;
  if (element && "showPicker" in element) {
    (element as any).showPicker();
  }
};

type ProjectSearchParams = {
  edit?: boolean;
};

export const Route = createFileRoute("/dashboard/admin/projects/$projectId/")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): ProjectSearchParams => {
    return {
      edit: search.edit === true || search.edit === "true",
    };
  },
});

type EditFormAttachment = NonNullable<ProjectModel["attachments"]>[number];

type EditFormState = {
  name: string;
  description: string;
  jobLocation: string | ProjectJobLocation | null;
  customerId: string;
  customerContactId: string;
  assignedManagerIds: string[];
  assignedSupportUserIds: string[];
  assignedAdminId: string;
  assignedAgencyId: string;
  status: string;
  isActive: boolean;
  type: "ongoing" | "one-time";
  startDate: string;
  endDate: string;
  notes: string;
  attachments: EditFormAttachment[];
};

function RouteComponent() {
  const { projectId } = Route.useParams();
  const navigate = Route.useNavigate();
  const { edit: isEditMode } = Route.useSearch();
  const { project } = useProjectContext();
  const { userProfile } = useAuth();
  const { users } = useUsers();
  const { agencies } = useAgencies();
  const { projectStatuses } = useProjectStatuses();
  const confirm = useConfirm();
  const { addContact, loading: contactLoading } = useCustomerOperations();

  const customerUsers = users.filter((u) => u.type === "customer");
  const managerUsers = users.filter((u) => u.type === "manager");
  const supportUsers = users.filter((u) => u.type === "support");
  const adminUsers = users.filter((u) => u.type === "admin");

  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]);
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [isDeletingSchedule, setIsDeletingSchedule] = useState(false);
  const [pendingScheduleData, setPendingScheduleData] = useState<any>(null);

  // Edit mode states
  const [editFormData, setEditFormData] = useState<EditFormState>({
    name: "",
    description: "",
    jobLocation: "",
    customerId: "",
    customerContactId: "",
    assignedManagerIds: [],
    assignedSupportUserIds: [],
    assignedAdminId: "",
    assignedAgencyId: "",
    status: "",
    isActive: true,
    type: "ongoing",
    startDate: "",
    endDate: "",
    notes: "",
    attachments: [],
  });
  const [isSaving, setIsSaving] = useState(false);
  const [useCustomerAddress, setUseCustomerAddress] = useState(false);
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const [newContactData, setNewContactData] = useState({
    title: "",
    name: "",
    email: "",
    phone: "",
    notes: "",
  });

  // Handle dialog open/close via URL state
  const setIsEditMode = useCallback(
    (open: boolean) => {
      navigate({
        to: "/dashboard/admin/projects/$projectId",
        params: { projectId },
        search: open ? { edit: true } : {},
        replace: true,
      });
    },
    [navigate, projectId],
  );

  // Initialize form data when project loads or edit mode opens
  useEffect(() => {
    if (project && isEditMode) {
      setEditFormData({
        name: project.name || "",
        description: project.description || "",
        jobLocation: project.jobLocation || "",
        customerId: project.customerId || "",
        customerContactId: project.customerContactId || "",
        assignedManagerIds: project.assignedManagerIds || [],
        assignedSupportUserIds: project.assignedSupportUserIds || [],
        assignedAdminId: project.assignedAdminId || "",
        assignedAgencyId: project.assignedAgencyId || "",
        status: project.statusId || "",
        isActive: project.isActive,
        type: project.type || "ongoing",
        startDate: project.startDate ? project.startDate.toDate().toISOString().split("T")[0] : "",
        endDate: project.endDate ? project.endDate.toDate().toISOString().split("T")[0] : "",
        notes: project.notes || "",
        attachments: project.attachments || [],
      });
    }
  }, [project, isEditMode]);

  // Schedule handling
  const handleScheduleChange = useCallback((scheduleData: any) => {
    setPendingScheduleData(scheduleData);
  }, []);

  const handleScheduleSave = useCallback(async () => {
    if (!project || !userProfile?.uid || !pendingScheduleData) return;

    try {
      await projectService.updateProject(
        projectId,
        {
          schedule: pendingScheduleData,
          hasSchedule: pendingScheduleData?.enabled || false,
        },
        userProfile.uid,
      );

      setIsScheduleDialogOpen(false);
      setPendingScheduleData(null);
      toast.success("Project schedule updated successfully!");
    } catch (error) {
      console.error("Error updating project schedule:", error);
      toast.error("Failed to update project schedule. Please try again.");
    }
  }, [project, projectId, userProfile?.uid, pendingScheduleData]);

  const handleScheduleCancel = useCallback(() => {
    setIsScheduleDialogOpen(false);
    setPendingScheduleData(null);
  }, []);

  const handleScheduleDelete = useCallback(async () => {
    if (!project || !userProfile?.uid) return;

    const confirmed = await confirm({
      title: "Delete Schedule?",
      description:
        "Are you sure you want to delete this project schedule? This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
    });

    if (!confirmed) return;

    setIsDeletingSchedule(true);
    try {
      await projectService.updateProject(
        projectId,
        {
          schedule: null,
          hasSchedule: false,
        },
        userProfile.uid,
      );

      setIsScheduleDialogOpen(false);
      setPendingScheduleData(null);
      toast.success("Project schedule deleted successfully!");
    } catch (error) {
      console.error("Error deleting project schedule:", error);
      toast.error("Failed to delete project schedule. Please try again.");
    } finally {
      setIsDeletingSchedule(false);
    }
  }, [project, projectId, userProfile?.uid, confirm]);

  // Attachment management functions
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (file.size > maxSize) {
      toast.error("File size must be less than 10MB");
      return;
    }

    const fileName = `${Date.now()}-${file.name}`;
    setUploadingFiles((prev) => [...prev, fileName]);

    try {
      const storageRef = ref(storage, `projects/attachments/${projectId}/${fileName}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      const newAttachment = {
        id: fileName,
        label: file.name,
        fileName: file.name,
        fileType: file.type,
        fileUrl: downloadURL,
        uploadedAt: Timestamp.fromDate(new Date()),
        size: file.size,
      };

      // Save directly to Firestore
      if (project && userProfile?.uid) {
        const updatedAttachments = [...(project.attachments || []), newAttachment];

        await projectService.updateProject(
          projectId,
          {
            attachments: updatedAttachments,
          },
          userProfile.uid,
        );

        toast.success("File uploaded successfully!");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("Failed to upload file");
    } finally {
      setUploadingFiles((prev) => prev.filter((f) => f !== fileName));
      if (event.target) {
        event.target.value = "";
      }
    }
  };

  const handleRemoveAttachment = async (attachmentId: string, fileUrl?: string) => {
    const confirmed = await confirm({
      title: "Delete Attachment?",
      description: "Are you sure you want to delete this attachment? This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
    });

    if (!confirmed) return;

    try {
      if (fileUrl) {
        try {
          const storageRef = ref(storage, fileUrl);
          await deleteObject(storageRef);
        } catch (storageError) {
          console.warn("Could not delete file from storage:", storageError);
        }
      }

      if (project && userProfile?.uid) {
        const updatedAttachments = (project.attachments || []).filter(
          (att) => att.id !== attachmentId,
        );

        await projectService.updateProject(
          projectId,
          {
            attachments: updatedAttachments,
          },
          userProfile.uid,
        );

        toast.success("Attachment deleted successfully!");
      }
    } catch (error) {
      console.error("Error removing attachment:", error);
      toast.error("Failed to remove attachment");
    }
  };

  // Edit form handlers
  const validateProjectForm = (): string[] => {
    const errors: string[] = [];

    if (!editFormData.name.trim()) {
      errors.push("Project name is required");
    }

    if (!editFormData.assignedManagerIds || editFormData.assignedManagerIds.length === 0) {
      errors.push("At least one manager is required");
    }

    if (editFormData.startDate && editFormData.endDate) {
      const start = new Date(editFormData.startDate);
      const end = new Date(editFormData.endDate);
      if (end < start) {
        errors.push("End date cannot be before start date");
      }
    }

    return errors;
  };

  const handleSaveProject = async () => {
    if (!project || !userProfile?.uid) return;

    // Validate form
    const errors = validateProjectForm();
    if (errors.length > 0) {
      toast.error(`Please fix the following errors:\n${errors.join("\n")}`);
      return;
    }

    setIsSaving(true);
    try {
      const startDateTimestamp =
        editFormData.startDate && editFormData.startDate.trim() !== ""
          ? Timestamp.fromDate(dateStringToNewYorkDate(editFormData.startDate))
          : null;
      const endDateTimestamp =
        editFormData.endDate && editFormData.endDate.trim() !== ""
          ? Timestamp.fromDate(dateStringToNewYorkDate(editFormData.endDate))
          : null;
      const isOngoing = editFormData.type === "ongoing";

      const updateData: Partial<
        Omit<ProjectModel, "id" | "createdAt" | "createdBy" | "updatedBy">
      > = {
        name: editFormData.name.trim(),
        description: editFormData.description.trim(),
        jobLocation: normalizeJobLocationForSave(editFormData.jobLocation),
        customerId: editFormData.customerId || null,
        customerContactId: editFormData.customerContactId || null,
        assignedManagerIds: editFormData.assignedManagerIds,
        assignedSupportUserIds: editFormData.assignedSupportUserIds,
        assignedAdminId: editFormData.assignedAdminId || null,
        assignedAgencyId: editFormData.assignedAgencyId || null,
        statusId: editFormData.status || "",
        isActive: editFormData.isActive,
        type: editFormData.type,
        startDate: startDateTimestamp,
        endDate: endDateTimestamp,
        notes: editFormData.notes,
        attachments: editFormData.attachments,
      };

      if (!isOngoing) {
        updateData.schedule = null;
        updateData.hasSchedule = false;
      }

      await projectService.updateProject(projectId, updateData, userProfile.uid);

      setIsEditMode(false);
      toast.success("Project updated successfully!");
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error updating project:", error);
      toast.error("Failed to update project. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
  };

  // Contact handling
  const handleAddNewContact = useCallback(async () => {
    if (!editFormData.customerId || !newContactData.name.trim()) {
      toast.error("Please fill in the name field");
      return;
    }

    try {
      const result = await addContact(editFormData.customerId, {
        title: newContactData.title.trim() || null,
        name: newContactData.name.trim(),
        email: newContactData.email.trim() || null,
        phone: newContactData.phone.trim() || null,
        notes: newContactData.notes.trim() || null,
      });
      if (result.success) {
        toast.success("Contact added successfully!");

        // Auto-select the newly created contact
        if (result.data?.contactId) {
          setEditFormData((prev) => ({
            ...prev,
            customerContactId: result.data.contactId,
          }));
        }

        setIsContactDialogOpen(false);
        setNewContactData({
          title: "",
          name: "",
          email: "",
          phone: "",
          notes: "",
        });
      } else {
        toast.error("Failed to add contact. Please try again.");
      }
    } catch (error) {
      console.error("Error adding contact:", error);
      toast.error("Failed to add contact. Please try again.");
    }
  }, [editFormData.customerId, newContactData, addContact]);

  const handleContactCancel = useCallback(() => {
    setIsContactDialogOpen(false);
    setNewContactData({
      title: "",
      name: "",
      email: "",
      phone: "",
      notes: "",
    });
  }, []);

  // Function to format customer address into a single string
  const formatCustomerAddress = useCallback(
    (customerId: string): string => {
      const customer = users.find((u) => u.uid === customerId);
      if (!customer?.address) return "";

      const parts = [
        customer.address.street,
        customer.address.address2,
        customer.address.city,
        customer.address.state,
        customer.address.zipCode,
        customer.address.country,
      ].filter(Boolean);

      return parts.join(", ");
    },
    [users],
  );

  // Handle use customer address checkbox
  const handleUseCustomerAddress = useCallback(
    (checked: boolean) => {
      setUseCustomerAddress(checked);

      if (checked && editFormData.customerId) {
        const customerAddress = formatCustomerAddress(editFormData.customerId);
        if (customerAddress) {
          const updatedLocation: ProjectJobLocation = {
            address: customerAddress,
            latitude: null,
            longitude: null,
          };

          // Preserve existing address notes if any
          if (
            isProjectJobLocation(editFormData.jobLocation) &&
            editFormData.jobLocation.addressNotes
          ) {
            updatedLocation.addressNotes = editFormData.jobLocation.addressNotes;
          }

          setEditFormData((prev) => ({
            ...prev,
            jobLocation: updatedLocation,
          }));
        }
      }
    },
    [editFormData.customerId, editFormData.jobLocation, formatCustomerAddress],
  );

  if (!project) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // Get related data
  const customer = users.find((u) => u.uid === project.customerId);
  const customerContact = customer?.contacts?.find(
    (c) => c.uniqueId === project.customerContactId || c.id === project.customerContactId,
  );

  return (
    <div className="flex flex-col">
      {/* Edit Dialog */}
      <Dialog open={isEditMode} onOpenChange={setIsEditMode}>
        <DialogContent className="flex max-h-[90vh] w-[95vw] max-w-3xl flex-col overflow-hidden p-0">
          <DialogHeader className="shrink-0 border-b px-4 py-3 sm:px-6">
            <DialogTitle>Edit Project</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
            <div className="grid gap-4 sm:grid-cols-2">
              {/* Project Name - Full width */}
              <div className="sm:col-span-2">
                <Label htmlFor="project-name">Project Name *</Label>
                <Input
                  id="project-name"
                  value={editFormData.name}
                  onChange={(e) =>
                    setEditFormData((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  placeholder="Enter project name"
                />
              </div>

              {/* Description - Full width */}
              <div className="sm:col-span-2">
                <Label htmlFor="description">Description</Label>
                <RichTextEditor
                  value={editFormData.description}
                  onChange={(html) =>
                    setEditFormData((prev) => ({
                      ...prev,
                      description: html,
                    }))
                  }
                  placeholder="Enter project description"
                  minHeight="100px"
                />
              </div>

              {/* Project Type */}
              <div>
                <Label htmlFor="type">Project Type</Label>
                <Select
                  value={editFormData.type}
                  onValueChange={(value) => {
                    setEditFormData((prev) => {
                      const newType = value as "ongoing" | "one-time";
                      const updated = {
                        ...prev,
                        type: newType,
                      };

                      // When switching to one-time and start date exists, set end date same as start
                      if (newType === "one-time" && prev.startDate) {
                        updated.endDate = prev.startDate;
                      }

                      return updated;
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select project type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ongoing">Ongoing</SelectItem>
                    <SelectItem value="one-time">One-time</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* SMS Notifications */}
              <div>
                <Label>SMS Notifications</Label>
                <Select
                  value={editFormData.isActive ? "active" : "inactive"}
                  onValueChange={(value) =>
                    setEditFormData((prev) => ({
                      ...prev,
                      isActive: value === "active",
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">
                      <span className="text-green-600">Active</span>
                    </SelectItem>
                    <SelectItem value="inactive">
                      <span className="text-red-600">Inactive</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Start Date */}
              <div>
                <Label htmlFor="startDate">Start Date</Label>
                <div className="relative">
                  <Input
                    id="startDate"
                    type="date"
                    value={editFormData.startDate}
                    onChange={(e) =>
                      setEditFormData((prev) => {
                        const updated = {
                          ...prev,
                          startDate: e.target.value,
                        };

                        // For one-time projects, automatically set end date same as start date
                        // Only if end date is not set or is before the new start date
                        if (prev.type === "one-time" && e.target.value) {
                          if (!prev.endDate || prev.endDate < e.target.value) {
                            updated.endDate = e.target.value;
                          }
                        }

                        return updated;
                      })
                    }
                    className={`pr-10 [&::-webkit-calendar-picker-indicator]:pointer-events-none [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-inner-spin-button]:hidden ${!editFormData.startDate ? "[&::-webkit-datetime-edit]:opacity-0 [&::-webkit-datetime-edit-day-field]:opacity-0 [&::-webkit-datetime-edit-fields-wrapper]:opacity-0 [&::-webkit-datetime-edit-month-field]:opacity-0 [&::-webkit-datetime-edit-text]:opacity-0 [&::-webkit-datetime-edit-year-field]:opacity-0" : ""}`}
                  />
                  {!editFormData.startDate && (
                    <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-gray-500">
                      Select date
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => triggerPicker("startDate")}
                    className={`absolute top-1/2 right-3 -translate-y-1/2 ${editFormData.startDate ? "text-gray-900" : "text-gray-400 hover:text-gray-600"}`}
                  >
                    <Calendar className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* End Date */}
              <div>
                <Label htmlFor="endDate">End Date</Label>
                <div className="relative">
                  <Input
                    id="endDate"
                    type="date"
                    value={editFormData.endDate}
                    onChange={(e) =>
                      setEditFormData((prev) => ({
                        ...prev,
                        endDate: e.target.value,
                      }))
                    }
                    className={`pr-10 [&::-webkit-calendar-picker-indicator]:pointer-events-none [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-inner-spin-button]:hidden ${!editFormData.endDate ? "[&::-webkit-datetime-edit]:opacity-0 [&::-webkit-datetime-edit-day-field]:opacity-0 [&::-webkit-datetime-edit-fields-wrapper]:opacity-0 [&::-webkit-datetime-edit-month-field]:opacity-0 [&::-webkit-datetime-edit-text]:opacity-0 [&::-webkit-datetime-edit-year-field]:opacity-0" : ""}`}
                  />
                  {!editFormData.endDate && (
                    <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-gray-500">
                      Select date
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => triggerPicker("endDate")}
                    className={`absolute top-1/2 right-3 -translate-y-1/2 ${editFormData.endDate ? "text-gray-900" : "text-gray-400 hover:text-gray-600"}`}
                  >
                    <Calendar className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Job Location - Full width */}
              <div className="sm:col-span-2">
                <Label>Job Location</Label>
                {editFormData.customerId && (
                  <div className="mb-2 flex items-center space-x-2">
                    <Checkbox
                      id="use-customer-address"
                      checked={useCustomerAddress}
                      onCheckedChange={handleUseCustomerAddress}
                    />
                    <label
                      htmlFor="use-customer-address"
                      className="cursor-pointer text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      Use customer address
                    </label>
                  </div>
                )}
                <PlacesAutocomplete
                  value={getJobLocationAddress(editFormData.jobLocation)}
                  onChange={(value, meta) => {
                    setEditFormData((prev) => {
                      const existingLocation = isProjectJobLocation(prev.jobLocation)
                        ? prev.jobLocation
                        : null;

                      const updatedLocation: ProjectJobLocation = {
                        address: value,
                        latitude: meta?.coordinates?.latitude ?? existingLocation?.latitude ?? null,
                        longitude:
                          meta?.coordinates?.longitude ?? existingLocation?.longitude ?? null,
                      };

                      if (existingLocation?.addressNotes) {
                        updatedLocation.addressNotes = existingLocation.addressNotes;
                      }

                      return {
                        ...prev,
                        jobLocation: updatedLocation,
                      };
                    });
                  }}
                  placeholder="Search for job location..."
                  types="geocode"
                  language="en"
                />
              </div>

              {/* Address Notes - Full width */}
              <div className="sm:col-span-2">
                <Label htmlFor="addressNotes">Address Notes</Label>
                <Input
                  id="addressNotes"
                  value={
                    isProjectJobLocation(editFormData.jobLocation)
                      ? (editFormData.jobLocation.addressNotes ?? "")
                      : ""
                  }
                  onChange={(e) => {
                    const notesValue = e.target.value;
                    setEditFormData((prev) => {
                      if (isProjectJobLocation(prev.jobLocation)) {
                        return {
                          ...prev,
                          jobLocation: {
                            ...prev.jobLocation,
                            addressNotes: notesValue,
                          },
                        };
                      }

                      const addressValue =
                        typeof prev.jobLocation === "string" ? prev.jobLocation : "";

                      return {
                        ...prev,
                        jobLocation: {
                          address: addressValue,
                          latitude: null,
                          longitude: null,
                          addressNotes: notesValue,
                        },
                      };
                    });
                  }}
                  placeholder="Suite number, door code, which door, etc."
                />
              </div>

              {/* Customer */}
              <div>
                <Label>Customer</Label>
                <SearchableSelectPopover
                  items={customerUsers}
                  selectedValue={editFormData.customerId}
                  onSelect={(value) => {
                    setEditFormData((prev) => ({
                      ...prev,
                      customerId: value || "",
                    }));
                    setUseCustomerAddress(false);
                  }}
                  getKey={(customer) => customer.uid}
                  getLabel={(customer) => `${customer.name} (${customer.email})`}
                  triggerLabel={
                    editFormData.customerId
                      ? users.find((u) => u.uid === editFormData.customerId)?.name ||
                        "Select customer"
                      : "Select customer"
                  }
                  placeholder="Search customers..."
                  emptyText="No customers found"
                  buttonClassName="w-full justify-between"
                  contentClassName="w-full p-0"
                />
              </div>

              {/* Customer Contact */}
              <div>
                <Label>Customer Contact</Label>
                {!editFormData.customerId ? (
                  <p className="text-sm text-gray-500">Select a customer first</p>
                ) : (
                  <>
                    <SearchableSelectPopover
                      items={(() => {
                        const customer = users.find((u) => u.uid === editFormData.customerId);
                        return customer?.contacts || [];
                      })()}
                      selectedValue={editFormData.customerContactId}
                      onSelect={(value) =>
                        setEditFormData((prev) => ({
                          ...prev,
                          customerContactId: value || "",
                        }))
                      }
                      getKey={(contact) => contact.uniqueId || contact.id}
                      getLabel={(contact) =>
                        [contact.name, contact.title, contact.email ? `<${contact.email}>` : null]
                          .filter(Boolean)
                          .join(" ")
                      }
                      triggerLabel={
                        editFormData.customerContactId
                          ? (() => {
                              const customer = users.find((u) => u.uid === editFormData.customerId);
                              const contact = customer?.contacts?.find(
                                (c) =>
                                  c.uniqueId === editFormData.customerContactId ||
                                  c.id === editFormData.customerContactId,
                              );
                              return contact
                                ? `${contact.name}${contact.email ? ` (${contact.email})` : ""}`
                                : "Select contact";
                            })()
                          : "Select contact"
                      }
                      placeholder="Search contacts..."
                      emptyText="No contacts found"
                      buttonClassName="w-full justify-between"
                      contentClassName="w-full p-0"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-2 w-full"
                      onClick={() => setIsContactDialogOpen(true)}
                    >
                      Add New Contact
                    </Button>
                  </>
                )}
              </div>

              {/* Agency */}
              <div>
                <Label>Agency</Label>
                <SearchableSelectPopover
                  items={agencies}
                  selectedValue={editFormData.assignedAgencyId || null}
                  onSelect={(value) =>
                    setEditFormData((prev) => ({
                      ...prev,
                      assignedAgencyId: value || "",
                    }))
                  }
                  getKey={(agency) => agency.id}
                  getLabel={(agency) => agency.name}
                  triggerLabel={
                    editFormData.assignedAgencyId
                      ? agencies.find((a) => a.id === editFormData.assignedAgencyId)?.name ||
                        "Select agency"
                      : "Select agency"
                  }
                  placeholder="Search agencies..."
                  emptyText="No agencies found"
                  buttonClassName="w-full justify-between"
                  contentClassName="w-full p-0"
                  allOption={{ value: null, label: "No agency assigned" }}
                />
              </div>

              {/* Status */}
              <div>
                <Label>Status</Label>
                <SearchableSelectPopover
                  items={projectStatuses}
                  selectedValue={editFormData.status || null}
                  onSelect={(value) =>
                    setEditFormData((prev) => ({
                      ...prev,
                      status: value || "",
                    }))
                  }
                  getKey={(status) => status.id}
                  getLabel={(status) => status.name}
                  triggerLabel={
                    editFormData.status
                      ? projectStatuses.find((s) => s.id === editFormData.status)?.name ||
                        "Select status"
                      : "Select status"
                  }
                  placeholder="Search status..."
                  emptyText="No status found"
                  buttonClassName="w-full justify-between"
                  contentClassName="w-full p-0"
                  allOption={{ value: null, label: "No status" }}
                />
              </div>

              {/* Managers - Full width */}
              <div className="sm:col-span-2">
                <Label>Assigned Managers *</Label>
                <MultiSelectCombobox
                  value={editFormData.assignedManagerIds}
                  onChange={(value) =>
                    setEditFormData((prev) => ({
                      ...prev,
                      assignedManagerIds: value,
                    }))
                  }
                  options={managerUsers.map((manager) => ({
                    value: manager.uid,
                    label: `${manager.name} (${manager.email})`,
                  }))}
                  placeholder="Select managers"
                  searchPlaceholder="Search managers..."
                />
              </div>

              {/* Support Users - Full width */}
              <div className="sm:col-span-2">
                <Label>Assigned Support Users</Label>
                <MultiSelectCombobox
                  value={editFormData.assignedSupportUserIds}
                  onChange={(value) =>
                    setEditFormData((prev) => ({
                      ...prev,
                      assignedSupportUserIds: value,
                    }))
                  }
                  options={supportUsers.map((support) => ({
                    value: support.uid,
                    label: `${support.name} (${support.email})`,
                  }))}
                  placeholder="Select support users"
                  searchPlaceholder="Search support users..."
                />
              </div>

              {/* Admin */}
              <div className="sm:col-span-2">
                <Label>Assigned Admin (Optional)</Label>
                <SearchableSelectPopover
                  items={adminUsers}
                  selectedValue={editFormData.assignedAdminId || null}
                  onSelect={(value) =>
                    setEditFormData((prev) => ({
                      ...prev,
                      assignedAdminId: value || "",
                    }))
                  }
                  getKey={(admin) => admin.uid}
                  getLabel={(admin) => `${admin.name} (${admin.email})`}
                  triggerLabel={
                    editFormData.assignedAdminId
                      ? users.find((u) => u.uid === editFormData.assignedAdminId)?.name ||
                        "Select admin"
                      : "Select admin"
                  }
                  placeholder="Search admins..."
                  emptyText="No admins found"
                  buttonClassName="w-full justify-between"
                  contentClassName="w-full p-0"
                  allOption={{ value: null, label: "No admin assigned" }}
                />
              </div>

              {/* Notes - Full width */}
              <div className="sm:col-span-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={editFormData.notes}
                  onChange={(e) =>
                    setEditFormData((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                  placeholder="Additional notes about this project"
                  className="min-h-[80px]"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="shrink-0 border-t px-4 py-3 sm:px-6">
            <Button variant="outline" onClick={handleCancelEdit} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSaveProject} disabled={isSaving}>
              <Save className="mr-2 h-4 w-4" />
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Contact Dialog */}
      <Dialog open={isContactDialogOpen} onOpenChange={setIsContactDialogOpen}>
        <DialogContent className="max-h-[90vh] w-[95vw] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Contact</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="contact-name">Name *</Label>
              <Input
                id="contact-name"
                value={newContactData.name}
                onChange={(e) => setNewContactData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Contact person's name"
              />
            </div>
            <div>
              <Label htmlFor="contact-title">Title</Label>
              <Input
                id="contact-title"
                value={newContactData.title}
                onChange={(e) =>
                  setNewContactData((prev) => ({
                    ...prev,
                    title: e.target.value,
                  }))
                }
                placeholder="e.g., Project Manager, Site Supervisor"
              />
            </div>
            <div>
              <Label htmlFor="contact-email">Email</Label>
              <Input
                id="contact-email"
                type="email"
                value={newContactData.email}
                onChange={(e) =>
                  setNewContactData((prev) => ({
                    ...prev,
                    email: e.target.value,
                  }))
                }
                placeholder="contact@example.com"
              />
            </div>
            <div>
              <Label htmlFor="contact-phone">Phone</Label>
              <Input
                id="contact-phone"
                value={newContactData.phone}
                onChange={(e) =>
                  setNewContactData((prev) => ({
                    ...prev,
                    phone: e.target.value,
                  }))
                }
                placeholder="(555) 123-4567"
              />
            </div>
            <div>
              <Label htmlFor="contact-notes">Notes</Label>
              <Textarea
                id="contact-notes"
                value={newContactData.notes}
                onChange={(e) =>
                  setNewContactData((prev) => ({
                    ...prev,
                    notes: e.target.value,
                  }))
                }
                placeholder="Additional notes about this contact"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleContactCancel}>
                Cancel
              </Button>
              <Button
                onClick={handleAddNewContact}
                disabled={contactLoading || !newContactData.name.trim()}
              >
                {contactLoading ? "Adding..." : "Add Contact"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tab Content */}
      <div className="mt-4">
        <div className="grid grid-cols-1 gap-4 min-[1024px]:grid-cols-2 min-[1024px]:gap-6">
          {/* Left Column - Details */}
          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <h2 className="mb-3 text-lg font-semibold text-gray-900">Details</h2>

            <div className="-mx-3 border-t border-gray-200"></div>

            <div className="mt-3 space-y-3">
              {/* Description */}
              <div>
                <label className="text-muted-foreground text-sm">Description</label>
                <HtmlContent content={project.description} className="mt-1 text-sm text-gray-900" />
              </div>

              <div className="-mx-3 border-t border-gray-200"></div>

              {/* Job Location */}
              <div>
                <label className="text-muted-foreground text-sm">Job Location</label>
                <p className="mt-1 text-sm text-gray-900">
                  {typeof project.jobLocation === "string"
                    ? project.jobLocation
                    : project.jobLocation?.address || "No location specified"}
                </p>
              </div>

              <div className="-mx-3 border-t border-gray-200"></div>

              {/* Customer details */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-muted-foreground text-sm">
                    Customer details{" "}
                    {customer && (
                      <Link
                        to="/dashboard/admin/customers/$customerId"
                        params={{ customerId: customer.uid }}
                        className="text-primary hover:underline"
                      >
                        {customer.name}
                      </Link>
                    )}
                  </label>
                  {customer && (
                    <Link
                      to="/dashboard/admin/customers/$customerId"
                      params={{ customerId: customer.uid }}
                    >
                      <Button variant="outline" size="sm">
                        Update Info
                      </Button>
                    </Link>
                  )}
                </div>
                {customer ? (
                  <div className="bg-muted space-y-1 rounded-md p-3">
                    {customer.email && (
                      <p className="text-sm text-gray-900">
                        <a
                          href={`mailto:${customer.email}`}
                          className="text-blue-600 hover:underline"
                        >
                          {customer.email}
                        </a>
                      </p>
                    )}
                    {customer.phoneNumbers && customer.phoneNumbers.length > 0 && (
                      <p className="text-muted-foreground text-sm">
                        Phone{" "}
                        {customer.phoneNumbers.map((phone, index) => (
                          <span key={phone}>
                            <a href={`tel:${phone}`} className="text-gray-900 hover:underline">
                              {phone}
                            </a>
                            {index < customer.phoneNumbers!.length - 1 && ", "}
                          </span>
                        ))}
                      </p>
                    )}
                    {customer.address && (
                      <p className="text-sm text-gray-900">
                        {[
                          customer.address.street,
                          customer.address.city,
                          customer.address.state,
                          customer.address.zipCode,
                          customer.address.country,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </p>
                    )}
                    {customer.website && (
                      <p className="text-muted-foreground text-sm">
                        Website{" "}
                        <a
                          href={customer.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          {customer.website}
                        </a>
                      </p>
                    )}
                    {customerContact && (
                      <p className="text-muted-foreground text-sm">
                        Customer contact{" "}
                        <span className="text-gray-900">{customerContact.name}</span>
                        {customerContact.email && (
                          <>
                            {" "}
                            <a
                              href={`mailto:${customerContact.email}`}
                              className="text-blue-600 hover:underline"
                            >
                              {customerContact.email}
                            </a>
                          </>
                        )}
                        {customerContact.phone && (
                          <>
                            {" "}
                            <a
                              href={`tel:${customerContact.phone}`}
                              className="text-gray-900 hover:underline"
                            >
                              {customerContact.phone}
                            </a>
                          </>
                        )}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-900">No customer assigned</p>
                )}
              </div>

              <div className="-mx-3 border-t border-gray-200"></div>

              {/* Attachments */}
              <div>
                <div className="mb-3 flex items-center justify-between">
                  <label className="text-muted-foreground text-sm">Attachments</label>
                  <label
                    htmlFor="file-upload"
                    className="text-muted-foreground inline-flex cursor-pointer items-center gap-2 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium transition-colors hover:bg-gray-50"
                  >
                    <Upload className="h-4 w-4" />
                    Upload File
                  </label>
                  <input
                    id="file-upload"
                    type="file"
                    className="hidden"
                    onChange={handleFileUpload}
                    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.zip,.rar,.txt,.xls,.xlsx,.csv"
                    disabled={uploadingFiles.length > 0}
                  />
                </div>
                <div className="space-y-2">
                  {uploadingFiles.map((fileName) => (
                    <div
                      key={fileName}
                      className="flex items-center gap-3 rounded-lg border bg-gray-50 p-3"
                    >
                      <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-blue-600"></div>
                      <span className="text-muted-foreground text-sm">Uploading {fileName}...</span>
                    </div>
                  ))}

                  {project.attachments && project.attachments.length > 0 ? (
                    project.attachments.map((file) => {
                      const extension = file.fileName?.split(".").pop()?.toUpperCase() || "TXT";
                      const sizeInMB = file.size ? (file.size / (1024 * 1024)).toFixed(2) : "0";
                      return (
                        <div
                          key={file.id}
                          className="flex items-center justify-between gap-3 rounded-lg border p-3 transition-colors hover:bg-gray-50"
                        >
                          <div className="flex items-center gap-3">
                            <FileIcon fileType={extension as any} size={40} />
                            <div className="flex flex-col">
                              {file.fileUrl ? (
                                <a
                                  href={file.fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-600 hover:underline"
                                >
                                  {file.label}
                                </a>
                              ) : (
                                <span className="text-sm text-gray-900">{file.label}</span>
                              )}
                              <span className="text-muted-foreground text-sm">{sizeInMB} MB</span>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveAttachment(file.id, file.fileUrl)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      );
                    })
                  ) : uploadingFiles.length === 0 ? (
                    <p className="text-muted-foreground text-sm">No attachments available</p>
                  ) : null}
                </div>
              </div>

              <div className="-mx-3 border-t border-gray-200"></div>

              {/* SMS Notifications */}
              <div>
                <label className="text-muted-foreground mb-2 block text-sm">
                  SMS Notifications
                </label>
                <div className="flex items-center gap-2">
                  {project.isActive ? (
                    <>
                      <div className="flex h-5 w-5 items-center justify-center rounded-full bg-green-100">
                        <svg
                          className="h-3 w-3 text-green-600"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <span className="text-sm text-gray-900">Enabled</span>
                    </>
                  ) : (
                    <>
                      <div className="flex h-5 w-5 items-center justify-center rounded-full bg-red-100">
                        <X className="h-3 w-3 text-red-600" />
                      </div>
                      <span className="text-sm text-gray-900">Disabled</span>
                    </>
                  )}
                </div>
              </div>

              <div className="-mx-3 border-t border-gray-200"></div>

              {/* Created At */}
              <div className="grid grid-cols-2 gap-x-8">
                <div>
                  <label className="text-muted-foreground mb-1 block text-sm">Created At</label>
                  <p className="text-sm text-gray-900">
                    {project.createdAt ? formatDateTimeShort(project.createdAt) : "Unknown"}
                  </p>
                </div>
                <div>
                  <label className="text-muted-foreground mb-1 block text-sm">Created by</label>
                  <p className="text-sm text-gray-900">
                    {project.createdBy
                      ? users.find((u) => u.uid === project.createdBy)?.name || "Unknown User"
                      : "Unknown"}
                  </p>
                </div>
              </div>

              <div className="-mx-6 border-t border-gray-200"></div>

              {/* Last Updated */}
              <div className="grid grid-cols-2 gap-x-8">
                <div>
                  <label className="text-muted-foreground mb-1 block text-sm">Last Updated</label>
                  <p className="text-sm text-gray-900">
                    {project.updatedAt ? formatDateTimeShort(project.updatedAt) : "Never updated"}
                  </p>
                </div>
                <div>
                  <label className="text-muted-foreground mb-1 block text-sm">Updated by</label>
                  <p className="text-sm text-gray-900">
                    {project.updatedBy
                      ? users.find((u) => u.uid === project.updatedBy)?.name || "Unknown User"
                      : "-"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Schedule & Work Units */}
          <div className="space-y-4">
            {/* Schedule Card - Only for ongoing projects */}
            {project.type === "ongoing" && (
              <div className="rounded-lg border border-gray-200 bg-white p-3">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">Schedule</h2>
                  <Dialog open={isScheduleDialogOpen} onOpenChange={setIsScheduleDialogOpen}>
                    <button
                      onClick={() => setIsScheduleDialogOpen(true)}
                      className="text-muted-foreground inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium transition-colors hover:bg-gray-50"
                    >
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 15 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M11.5566 1.94368C11.7516 1.74865 12.0185 1.63867 12.2972 1.63867C12.5758 1.63867 12.8427 1.74865 13.0377 1.94368C13.2328 2.1387 13.3427 2.40559 13.3427 2.68424C13.3427 2.96289 13.2328 3.22978 13.0377 3.4248L4.31021 12.1523L1.63867 12.8895L2.37585 10.218L11.5566 1.94368Z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      {project.hasSchedule ? "Edit" : "Set up"}
                    </button>
                    <DialogContent className="max-h-[90vh] w-[95vw] max-w-2xl overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Configure Project Schedule</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <ScheduleForm
                          schedule={project.schedule}
                          projectStartDate={project.startDate ?? null}
                          projectEndDate={project.endDate ?? null}
                          onChange={handleScheduleChange}
                        />
                        <div className="flex justify-between gap-2">
                          {project.hasSchedule && project.schedule && (
                            <Button
                              variant="destructive"
                              onClick={handleScheduleDelete}
                              disabled={isDeletingSchedule}
                            >
                              {isDeletingSchedule ? "Deleting..." : "Delete Schedule"}
                            </Button>
                          )}
                          <div className="ml-auto flex justify-end gap-2">
                            <Button
                              variant="outline"
                              onClick={handleScheduleCancel}
                              disabled={isDeletingSchedule}
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={handleScheduleSave}
                              disabled={!pendingScheduleData || isDeletingSchedule}
                            >
                              Save Schedule
                            </Button>
                          </div>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>

                <div className="-mx-3 mb-3 border-t border-gray-200"></div>

                {project.hasSchedule && project.schedule ? (
                  <div className="relative rounded-md bg-blue-50 p-2">
                    <div className="absolute top-3 bottom-3 left-3 w-1 rounded-full bg-blue-500"></div>
                    <div className="space-y-1 pl-7">
                      {project.schedule.type === "preset" ? (
                        <p className="text-sm text-gray-600">
                          <span className="text-gray-500">Recurring</span> {project.schedule.preset}
                          {project.schedule.interval > 1 &&
                            ` every ${project.schedule.interval} ${project.schedule.frequency}`}
                        </p>
                      ) : (
                        <p className="text-sm text-gray-600">
                          <span className="text-gray-500">Recurring</span>{" "}
                          {project.schedule.frequency}
                          {project.schedule.interval > 1 && ` every ${project.schedule.interval}`}
                          {project.schedule.daysOfWeek &&
                            project.schedule.daysOfWeek.length > 0 && (
                              <span>
                                {" "}
                                on{" "}
                                {project.schedule.daysOfWeek
                                  .map(
                                    (day) => ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][day],
                                  )
                                  .join(", ")}
                              </span>
                            )}
                        </p>
                      )}
                      {project.schedule.scheduleStartDate && (
                        <p className="text-sm text-gray-600">
                          <span className="text-gray-500">Starting</span>{" "}
                          {project.schedule.scheduleStartDate.toDate().toLocaleDateString()}
                          {project.schedule.scheduleEndDate && (
                            <span>
                              {" "}
                              - {project.schedule.scheduleEndDate.toDate().toLocaleDateString()}
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">Not set</p>
                )}
              </div>
            )}

            {/* Work Units Card */}
            <WorkUnitsSection
              projectId={projectId}
              managerUid={project.assignedManagerIds?.[0]}
              readonly={false}
              className="border-none shadow-none"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
